import { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  buildAdminReviewsHandlers,
  buildUpdateUrl,
  parsePageParam,
  parseSortParam,
  usePeriods,
} from '../components/pages/adminReviewsPageHelpers'
import useReviewsQuery, { ReviewQueryOpts } from './useReviewsQuery'
import { useQuery } from './useQuery'

type FetchPage<TRow, TSort extends string> = (
  page: number,
  size: number,
  opts: ReviewQueryOpts<TSort>
) => Promise<{ content: TRow[]; totalElements: number }>

type ExportFn<TSort extends string> = (opts?: ReviewQueryOpts<TSort>) => Promise<void>

type Config<TRow extends { reviewerName: string }, TSort extends string> = {
  sortFields: readonly string[]
  fetchPage: FetchPage<TRow, TSort>
  exportFn: ExportFn<TSort>
  errorMessage: string
}

const useAdminReviewsPage = <TRow extends { reviewerName: string }, TSort extends string>(
  config: Config<TRow, TSort>
) => {
  const locationQuery = useQuery()
  const history = useHistory()

  const periodIdParam = locationQuery.get('periodId')
  const reviewerIdParam = locationQuery.get('reviewerId')
  const searchParam = locationQuery.get('search') ?? ''
  const reviewerNameParam = locationQuery.get('reviewerName') ?? ''
  const sortParam = parseSortParam(locationQuery.get('sort'), config.sortFields) as TSort | undefined
  const [initialPage] = useState(() => parsePageParam(locationQuery.get('page')))

  const periods = usePeriods()
  const periodId = periodIdParam ? Number(periodIdParam) : undefined
  const reviewerId = reviewerIdParam ? Number(reviewerIdParam) : undefined

  const [refreshKey, setRefreshKey] = useState(0)
  const reviews = useReviewsQuery<TRow, TSort>({
    refreshKey,
    fetchPage: config.fetchPage,
    errorMessage: config.errorMessage,
    periodId,
    reviewerId,
    initialSearch: searchParam,
    initialReviewerName: reviewerNameParam,
    sort: sortParam,
    initialPage,
  })

  const updateUrl = buildUpdateUrl(locationQuery, search => history.replace({ search }))
  const handlers = buildAdminReviewsHandlers<TSort>(reviews, updateUrl)

  const handleExport = () =>
    config.exportFn({
      periodId,
      reviewerId,
      search: searchParam,
      reviewerName: reviewerNameParam,
      sort: sortParam,
    })

  const reviewerPillName = useMemo(() => {
    if (!reviewerId) return null
    const firstRow = reviews.rows[0]
    return firstRow?.reviewerName || null
  }, [reviewerId, reviews.rows])

  const refresh = () => setRefreshKey(k => k + 1)

  const filtersProps = {
    periods,
    periodId,
    search: reviews.search,
    reviewerName: reviews.reviewerName,
    reviewerId: reviewerId ?? null,
    reviewerPillName,
    onPeriodChange: handlers.handlePeriodChange,
    onSearchChange: handlers.handleSearchChange,
    onReviewerNameChange: handlers.handleReviewerNameChange,
    onClearReviewer: handlers.handleClearReviewer,
    onExport: handleExport,
    exportDisabled: reviews.rows.length === 0,
  }

  const tableProps = {
    rows: reviews.rows,
    loading: reviews.loading,
    error: reviews.error,
    page: reviews.page,
    pageSize: reviews.pageSize,
    totalElements: reviews.totalElements,
    sort: sortParam,
    onPageChange: handlers.handlePageChange,
    onPageSizeChange: handlers.handlePageSizeChange,
    onSortChange: handlers.handleSortChange,
    onRetry: refresh,
  }

  return { filtersProps, tableProps, refresh }
}

export default useAdminReviewsPage
